import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from '../../config/apiConfig';

const clearStorage = () => ['user', 'token', 'accessToken', 'refreshToken', 'sessionId'].forEach(key => localStorage.removeItem(key));
const empty = () => ({ user: null, token: null, refreshToken: null, sessionId: null, isAuthenticated: false, status: 'idle', error: null, loginRequestId: null, sessionRevision: nanoid() });
const readSession = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (!user || !token) return empty();
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) { sessionId = nanoid(); localStorage.setItem('sessionId', sessionId); }
    return { ...empty(), user, token, refreshToken: localStorage.getItem('refreshToken'), sessionId, isAuthenticated: true, status: 'succeeded' };
  } catch { return empty(); }
};
const persist = state => {
  localStorage.setItem('user', JSON.stringify(state.user));
  localStorage.setItem('token', state.token);
  localStorage.setItem('accessToken', state.token);
  if (state.refreshToken) localStorage.setItem('refreshToken', state.refreshToken);
  else localStorage.removeItem('refreshToken');
  localStorage.setItem('sessionId', state.sessionId);
};
const startSession = (state, payload) => {
  Object.assign(state, empty(), { user: payload.user, token: payload.token || payload.accessToken, refreshToken: payload.refreshToken || null, sessionId: nanoid(), isAuthenticated: true, status: 'succeeded' });
  persist(state);
};
export const logoutSession = () => async (dispatch, getState) => {
  const { refreshToken } = getState().auth;
  dispatch(logout());
  try {
    if (refreshToken) await fetch(`${API_URL}/auth/logout`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken }) });
  } catch { /* The local session is already closed. */ }
};
export const login = createAsyncThunk('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const data = await response.json();
    if (!response.ok) return rejectWithValue(data.message);
    const token = data.accessToken || data.token;
    if (!token || !data.user) return rejectWithValue('Resposta de autenticação inválida.');
    return { user: data.user, token, refreshToken: data.refreshToken || null };
  } catch (error) { return rejectWithValue(error.message || 'Não foi possível conectar ao servidor'); }
});
const authSlice = createSlice({
  name: 'auth', initialState: readSession(),
  reducers: {
    logout(state) { Object.assign(state, empty()); clearStorage(); },
    initializeAuth(state) { const restored = readSession(); if (restored.sessionId === state.sessionId) restored.sessionRevision = state.sessionRevision; Object.assign(state, restored); },
    updateUser(state, { payload }) {
      if (!state.isAuthenticated || String(payload.user?._id || payload.user?.id) !== String(state.user?._id || state.user?.id)) return;
      state.user = payload.user;
      if (payload.token) state.token = payload.token;
      persist(state);
    },
    updateTokens(state, { payload }) {
      if (!state.isAuthenticated || (payload.sessionId && payload.sessionId !== state.sessionId)) return;
      state.token = payload.accessToken;
      state.refreshToken = payload.refreshToken || null;
      persist(state);
    },
    setAuthFromToken(state, { payload }) {
      const token = typeof payload === 'string' ? payload : (payload?.token || payload?.accessToken);
      if (!token) return;
      const decoded = jwtDecode(token);
      startSession(state, { token, refreshToken: payload?.refreshToken, user: payload?.user || { _id: decoded.userId, name: decoded.name, avatar: decoded.avatar } });
    },
  },
  extraReducers: builder => builder
    .addCase(login.pending, (state, action) => { Object.assign(state, empty(), { status: 'loading', loginRequestId: action.meta.requestId }); clearStorage(); })
    .addCase(login.fulfilled, (state, action) => { if (state.loginRequestId === action.meta.requestId) startSession(state, action.payload); })
    .addCase(login.rejected, (state, action) => { if (state.loginRequestId === action.meta.requestId) { Object.assign(state, empty(), { status: 'failed', error: action.payload }); clearStorage(); } }),
});
export const { logout, initializeAuth, setAuthFromToken, updateUser, updateTokens } = authSlice.actions;
export default authSlice.reducer;
